/**
 * `StackedBar` — a single horizontal bar split into segments (e.g. flag counts: High/
 * Moderate/Watch), each segment's width proportional to its share of the total. Hand-built
 * SVG in the same register as `RankedBarChart`/`HistogramChart` (the repo forbids chart
 * libraries): viewBox-scaled, `role="img"` with a full plain-text readout, a legend below.
 */
import { fmtNum } from '../../../lib/format';
import { NEUTRAL_COLOR } from '../uc1Constants';

export const SB_W = 394;
export const SB_H = 22;

export interface StackedBarSegment {
  id: string;
  label: string;
  value: number;
  color: string;
}

export interface StackedBarProps {
  segments: StackedBarSegment[];
  ariaLabel: string;
  height?: number;
  /**
   * Unit printed after every segment figure (legend, tooltip and readout). Omitted by
   * default, because the usual segment value is a plain count of watersheds; a caller whose
   * segments are shares (main crops, already × 100) passes `"%"` so the legend does not
   * read as a count of eleven watersheds when it means eleven percent.
   */
  unit?: string;
}

// The one neutral grey, from `uc1Constants.ts`. Copied by hand until 3 Oct 2026, when
// the client had it darkened and every copy went stale in a different place.
const NEUTRAL_GREY = NEUTRAL_COLOR;

/** One segment's figure with its unit, if the caller gave one. */
function withUnit(value: number, unit?: string): string {
  return unit ? `${fmtNum(value, 0)} ${unit}` : fmtNum(value, 0);
}

/** The chart's own `aria-label` — a plain-text readout of every segment's figure. */
function readout(ariaLabel: string, segments: StackedBarSegment[], unit?: string): string {
  const parts = segments.map((s) => `${s.label} ${withUnit(s.value, unit)}`);
  return `${ariaLabel} — ${parts.join(', ')}`;
}

export function StackedBar({ segments, ariaLabel, height = SB_H, unit }: StackedBarProps) {
  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0);
  let x = 0;

  return (
    <>
      <svg
        className="intel-chart sb-chart"
        viewBox={`0 0 ${SB_W} ${height}`}
        role="img"
        aria-label={readout(ariaLabel, segments, unit)}
        data-testid="stacked-bar-chart"
      >
        {total <= 0 ? (
          <rect x={0} y={0} width={SB_W} height={height} rx={3} fill={NEUTRAL_GREY} data-empty="" />
        ) : (
          segments.map((seg) => {
            if (seg.value <= 0) return null;
            const w = (seg.value / total) * SB_W;
            const rect = (
              <rect
                key={seg.id}
                x={x}
                y={0}
                width={w}
                height={height}
                data-segment={seg.id}
                fill={seg.color}
              >
                <title>{`${seg.label}: ${withUnit(seg.value, unit)}`}</title>
              </rect>
            );
            x += w;
            return rect;
          })
        )}
      </svg>
      <ul className="chart-legend">
        {segments.map((seg) => (
          <li key={seg.id}>
            <span className="swatch" style={{ background: seg.color }} />
            <span className="legend-label">{seg.label}</span>
            <span className="legend-count">{withUnit(seg.value, unit)}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

export default StackedBar;
